import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingUp, FiClock, FiCheckCircle, FiAlertCircle, FiDownload, FiEye, FiCalendar } = FiIcons;

const Dashboard = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('all');

  const completedAssessments = [
    {
      id: 1,
      title: 'Business Pulse Check',
      category: 'Strategy',
      completedDate: '2024-01-15',
      score: 78,
      status: 'completed',
      reportAvailable: true
    },
    {
      id: 2,
      title: 'Marketing Effectiveness Audit',
      category: 'Marketing',
      completedDate: '2024-01-10',
      score: 65,
      status: 'completed',
      reportAvailable: true
    },
    {
      id: 3,
      title: 'Financial Health Assessment',
      category: 'Finance',
      completedDate: '2024-01-05',
      score: 82,
      status: 'completed',
      reportAvailable: true
    }
  ];

  const upcomingAssessments = [
    {
      id: 4,
      title: 'Sales Operations Review',
      category: 'Sales',
      scheduledDate: '2024-01-25',
      hasExpertReview: true
    }
  ];

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const stats = [
    {
      title: 'Assessments Completed',
      value: completedAssessments.length,
      icon: FiCheckCircle,
      color: 'text-green-600 bg-green-100'
    },
    {
      title: 'Average Score',
      value: Math.round(completedAssessments.reduce((acc, assessment) => acc + assessment.score, 0) / completedAssessments.length),
      icon: FiTrendingUp,
      color: 'text-blue-600 bg-blue-100'
    },
    {
      title: 'Upcoming Reviews',
      value: upcomingAssessments.length,
      icon: FiClock,
      color: 'text-orange-600 bg-orange-100'
    },
    {
      title: 'Reports Available',
      value: completedAssessments.filter(a => a.reportAvailable).length,
      icon: FiDownload,
      color: 'text-purple-600 bg-purple-100'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Assessment Dashboard
          </h1>
          <p className="text-gray-600">
            Track your business assessment progress and access your reports
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {stats.map((stat, index) => (
            <div key={stat.title} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    {stat.title}
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {stat.value}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <SafeIcon icon={stat.icon} className="w-6 h-6" />
                </div>
              </div>
            </div>
          ))}
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Completed Assessments */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Completed Assessments
                </h2>
                <select
                  value={selectedTimeframe}
                  onChange={(e) => setSelectedTimeframe(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Time</option>
                  <option value="month">This Month</option>
                  <option value="quarter">This Quarter</option>
                </select>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {completedAssessments.map((assessment) => (
                  <div key={assessment.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 mb-1">
                          {assessment.title}
                        </h3>
                        <p className="text-sm text-gray-500 mb-2">
                          {assessment.category} • Completed {assessment.completedDate}
                        </p>
                        <div className="flex items-center gap-3">
                          <div className={`px-2 py-1 rounded-full text-sm font-medium ${getScoreColor(assessment.score)}`}>
                            Score: {assessment.score}%
                          </div>
                          {assessment.reportAvailable && (
                            <span className="text-green-600 text-sm flex items-center">
                              <SafeIcon icon={FiCheckCircle} className="w-4 h-4 mr-1" />
                              Report Ready
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                        <SafeIcon icon={FiEye} className="w-4 h-4 mr-1" />
                        View Report
                      </button>
                      <button className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <SafeIcon icon={FiDownload} className="w-4 h-4 mr-1" />
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Upcoming Assessments & Quick Actions */}
          <div className="space-y-6">
            {/* Upcoming Assessments */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200"
            >
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  Upcoming Reviews
                </h2>
              </div>
              
              <div className="p-6">
                {upcomingAssessments.length > 0 ? (
                  <div className="space-y-4">
                    {upcomingAssessments.map((assessment) => (
                      <div key={assessment.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 mb-1">
                              {assessment.title}
                            </h3>
                            <p className="text-sm text-gray-500 mb-2">
                              {assessment.category}
                            </p>
                            <div className="flex items-center text-sm text-gray-600">
                              <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-1" />
                              Scheduled: {assessment.scheduledDate}
                            </div>
                            {assessment.hasExpertReview && (
                              <div className="flex items-center text-sm text-blue-600 mt-1">
                                <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-1" />
                                Expert review included
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <SafeIcon icon={FiCalendar} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No upcoming assessments scheduled</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200"
            >
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  Quick Actions
                </h2>
              </div>
              
              <div className="p-6 space-y-3">
                <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                  Browse New Assessments
                </button>
                <button className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors">
                  Schedule Expert Consultation
                </button>
                <button className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors">
                  Download All Reports
                </button>
              </div>
            </motion.div>

            {/* Performance Overview */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200"
            >
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  Performance Trends
                </h2>
              </div>
              
              <div className="p-6">
                <div className="text-center py-8">
                  <SafeIcon icon={FiTrendingUp} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-2">Performance analytics coming soon</p>
                  <p className="text-sm text-gray-400">
                    Complete more assessments to see trends and insights
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;